import { datadogRum } from '@datadog/browser-rum';
import { createJSONStorage } from 'jotai/utils';
import { z } from 'zod';

export const TrainTogetherModeDataSchema = z.object({
  estimatedMinutes: z.number(),
  languageSelected: z.string(),
  planId: z.string(),
  planLabel: z.string(),
  planName: z.string(),
  selectedTeamMemberIds: z.array(z.string()),
});

export const TrainTogetherModeStateSchema = z.object({
  activePlanId: z.string(),
  isTrainTogetherModeOpen: z.boolean(),
  hasTeamMembersAssignedToPlan: z.boolean(),
  showProgressModal: z.boolean(),
  showTrainTogetherPopup: z.boolean(),
  isTrainTogetherMode: z.boolean(),
  isBeforeNavigate: z.boolean(),
  allAssigneesCompleted: z.boolean(),
  trainTogetherModeData: TrainTogetherModeDataSchema,
});

export type TrainTogetherModeData = z.infer<typeof TrainTogetherModeDataSchema>;
export type TrainTogetherModeState = z.infer<
  typeof TrainTogetherModeStateSchema
>;

export const DEFAULT_TRAIN_TOGETHER_MODE_DATA =
  TrainTogetherModeDataSchema.parse({
    estimatedMinutes: 0,
    languageSelected: '',
    planId: '',
    planLabel: '',
    planName: '',
    selectedTeamMemberIds: [],
  });

export const DEFAULT_TRAIN_TOGETHER_MODE_STATE =
  TrainTogetherModeStateSchema.parse({
    activePlanId: '',
    isTrainTogetherModeOpen: false,
    hasTeamMembersAssignedToPlan: false,
    showProgressModal: false,
    showTrainTogetherPopup: false,
    isTrainTogetherMode: false,
    isBeforeNavigate: false,
    allAssigneesCompleted: false,
    trainTogetherModeData: DEFAULT_TRAIN_TOGETHER_MODE_DATA,
  });

const parseNonEmptyStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(
    (id): id is string => typeof id === 'string' && id.length > 0,
  );
};

const PersistedTrainTogetherModeDataSchema = z
  .object({
    estimatedMinutes: z.unknown().optional(),
    languageSelected: z.unknown().optional(),
    planId: z.unknown().optional(),
    planLabel: z.unknown().optional(),
    planName: z.unknown().optional(),
    selectedTeamMemberIds: z.unknown().optional(),
  })
  .loose()
  .transform(data =>
    TrainTogetherModeDataSchema.parse({
      estimatedMinutes:
        typeof data.estimatedMinutes === 'number' ? data.estimatedMinutes : 0,
      languageSelected:
        typeof data.languageSelected === 'string' ? data.languageSelected : '',
      planId: typeof data.planId === 'string' ? data.planId : '',
      planLabel: typeof data.planLabel === 'string' ? data.planLabel : '',
      planName: typeof data.planName === 'string' ? data.planName : '',
      selectedTeamMemberIds: parseNonEmptyStringArray(
        data.selectedTeamMemberIds,
      ),
    }),
  );

const PersistedTrainTogetherModeStateSchema = z
  .object({
    activePlanId: z.unknown().optional(),
    isTrainTogetherModeOpen: z.unknown().optional(),
    hasTeamMembersAssignedToPlan: z.unknown().optional(),
    showProgressModal: z.unknown().optional(),
    showTrainTogetherPopup: z.unknown().optional(),
    isTrainTogetherMode: z.unknown().optional(),
    isBeforeNavigate: z.unknown().optional(),
    allAssigneesCompleted: z.unknown().optional(),
    trainTogetherModeData: z.unknown().optional(),
  })
  .loose()
  .transform(data => {
    const modeData = PersistedTrainTogetherModeDataSchema.safeParse(
      data.trainTogetherModeData,
    );

    return TrainTogetherModeStateSchema.parse({
      activePlanId:
        typeof data.activePlanId === 'string' ? data.activePlanId : '',
      isTrainTogetherModeOpen: data.isTrainTogetherModeOpen === true,
      hasTeamMembersAssignedToPlan: data.hasTeamMembersAssignedToPlan === true,
      showProgressModal: data.showProgressModal === true,
      showTrainTogetherPopup: data.showTrainTogetherPopup === true,
      isTrainTogetherMode: data.isTrainTogetherMode === true,
      isBeforeNavigate: data.isBeforeNavigate === true,
      allAssigneesCompleted: data.allAssigneesCompleted === true,
      trainTogetherModeData: modeData.success
        ? modeData.data
        : DEFAULT_TRAIN_TOGETHER_MODE_DATA,
    });
  });

const parseTrainTogetherModeState = (
  parsed: unknown,
): TrainTogetherModeState => {
  const result = PersistedTrainTogetherModeStateSchema.safeParse(parsed);
  return result.success ? result.data : DEFAULT_TRAIN_TOGETHER_MODE_STATE;
};

// Train Together state moved from `app.training.mode` to
// `app.trainTogether.mode` with renamed fields. We don't migrate the old shape
// — drop it and let users start from defaults. A mid-session reset across this
// one deploy is acceptable; the alternative (copying renamed fields) is far
// more code for a transient edge case.
if (typeof localStorage !== 'undefined') {
  localStorage.removeItem('app.training.mode');
}

const baseStorage = createJSONStorage<TrainTogetherModeState>(
  () => localStorage,
);

export const trainTogetherModeStorage: typeof baseStorage = {
  ...baseStorage,
  getItem: (key, initialValue) => {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return initialValue;
    }

    try {
      const parsed: unknown = JSON.parse(raw);
      return parseTrainTogetherModeState(parsed);
    } catch (error) {
      localStorage.removeItem(key);
      datadogRum.addError(
        error instanceof Error
          ? error
          : new Error('Failed to parse train together mode storage'),
      );
      return initialValue;
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      const isQuotaExceeded =
        error instanceof DOMException &&
        (error.name === 'QuotaExceededError' ||
          error.name === 'NS_ERROR_DOM_QUOTA_REACHED');

      datadogRum.addError(
        error instanceof Error
          ? error
          : new Error('Failed to persist train together mode storage'),
        {
          quotaExceeded: isQuotaExceeded,
          selectedTeamMemberCount:
            value.trainTogetherModeData.selectedTeamMemberIds.length,
        },
      );
    }
  },
  removeItem: key => {
    localStorage.removeItem(key);
  },
};
